import React from 'react'

import type { SvgComponent } from '@sketch/icons'
import { ReactComponent as ScopeDisabledIcon } from '@sketch/icons/forbidden-16-svg'
import { ReactComponent as WorkspaceReadIcon } from '@sketch/icons/workspace-16-svg'
import { ReactComponent as DocumentReadIcon } from '@sketch/icons/documents-prism-16-svg'
import { ReactComponent as UpdateReadIcon } from '@sketch/icons/clock-16-svg'
import { ReactComponent as PageReadIcon } from '@sketch/icons/document-16-svg'
import { ReactComponent as ArtboardReadIcon } from '@sketch/icons/artboard-16-svg'
import { ReactComponent as UpdateExportIcon } from '@sketch/icons/arrow-down-to-line-16-svg'

import { PersonalTokenFragment } from '@sketch/gql-types'

import { ScopeContainer, Icon } from './AccessScope.styles'

export type PublicApiAccessScope = PersonalTokenFragment['accessScopes'][number]

export const allScopes: PublicApiAccessScope[] = [
  'WORKSPACE_READ',
  'DOCUMENT_READ',
  'UPDATE_READ',
  'PAGE_READ',
  'ARTBOARD_READ',
  'UPDATE_EXPORT',
]

const scopeMetadata: {
  [key in PublicApiAccessScope]: {
    description: string
    icon: {
      src: SvgComponent
      alt: string
    }
  }
} = {
  WORKSPACE_READ: {
    description: 'Access to your Workspaces',
    icon: {
      src: WorkspaceReadIcon,
      alt: 'Workspace icon',
    },
  },
  DOCUMENT_READ: {
    description: 'Read-only access to documents ',
    icon: {
      src: DocumentReadIcon,
      alt: 'Document icon',
    },
  },
  UPDATE_READ: {
    description: 'Read-only access to document updates',
    icon: {
      src: UpdateReadIcon,
      alt: 'Update icon',
    },
  },
  PAGE_READ: {
    description: 'Read-only access to document pages',
    icon: {
      src: PageReadIcon,
      alt: 'Page icon',
    },
  },
  ARTBOARD_READ: {
    description: 'Read-only access to page Artboards',
    icon: {
      src: ArtboardReadIcon,
      alt: 'Artboard icon',
    },
  },
  UPDATE_EXPORT: {
    description: 'Asset exports for document updates',
    icon: {
      src: UpdateExportIcon,
      alt: 'Export icon',
    },
  },
}

type AccessScopeProps = {
  scope: PublicApiAccessScope
  enabled: boolean
}

export const descriptionForScope = (scope: PublicApiAccessScope): string => {
  const { description } = scopeMetadata[scope]
  return description
}

const AccessScope: React.FC<AccessScopeProps> = props => {
  const { scope, enabled } = props
  const { description, icon } = scopeMetadata[scope]
  const iconSrc = enabled ? icon.src : ScopeDisabledIcon
  const altText = enabled ? icon.alt : 'Disabled icon'
  return (
    <ScopeContainer key={scope} enabled={enabled}>
      <Icon as={iconSrc} aria-label={altText} enabled={enabled} />
      <span>{description}</span>
    </ScopeContainer>
  )
}

export default AccessScope
